import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Modal,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  CircularProgress,
  Popper,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import PositiveIcon from "../../assets/meeting/positive-icon.svg";
import TopicSentimentIcon from "../../assets/meeting/topic-sentiment-icon.svg";
import "../../styles/feedback.css";
import InteractionFeedbackService from "../../services/interactions/InteractionFeedbackService";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { GlobalContext } from "../../context/GlobalContext";

interface FeedbackModalProps {
  open: boolean;
  interactionId: string;
  badgeData?: {
    topicSentiment?: string;
    topicClassification?: string;
    isPositive?: boolean;
    isNegative?: boolean;
    isNeutral?: boolean;
    classificationColor?: string | null;
  };
  onClose: () => void;
  onSubmit: (data: any) => void;
  feedbackKey: "topic" | "action_item" | "abstraction_insights" | "abstraction_call_quality" | `abstraction_call_quality_${string}` | "prep_notes" | "client_action_item";
  score?: string;
  executiveAttentionNeeded?: string;
  answer?: string | null;
  feedbackReference?: string;
  anchorEl?: HTMLElement | null;
}

const FeedbackPopup: React.FC<FeedbackModalProps> = ({
  open,
  interactionId,
  badgeData,
  onClose,
  onSubmit,
  feedbackKey,
  score,
  executiveAttentionNeeded,
  answer,
  feedbackReference,
  anchorEl,
}) => {
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedSubOption, setSelectedSubOption] = useState("");
  const [comment, setComment] = useState("");
  const [initialValues, setInitialValues] = useState({
    option: "",
    subOption: "",
    comment: "",
  });
  const [showOptionError, setShowOptionError] = useState(false);
  const [showSubOptionError, setShowSubOptionError] = useState(false);
  const [showCommentError, setShowCommentError] = useState(false);
  const { showSnackbar } = useDisplaySnackbar();
  const { impersonationDetails } = useContext(GlobalContext);
  const isImpersonating = impersonationDetails?.isImpersonating === true;

  const isActionItemFeedback =
    feedbackKey === "action_item" ||
    feedbackKey === "client_action_item";

  const { 
    isLoading: isOptionsLoading,
    isFetching: isOptionsFetching,
    isSuccess: isOptionSuccess,
    isError: isOptionsError,
    error: optionsError,
    data: feedbackOptionsData,
  } = InteractionFeedbackService.useGetInteractionFeedbackOptions(true);

  const {
    isLoading: isFeedbackLoading,
    isFetching: isFeedbackFetching,
    isSuccess: isFeedbackSuccess,
    isError: isFeedbackError,
    error: feedbackError,
    data: existingFeedback,
    refetch: refetchFeedback
  } = InteractionFeedbackService.useGetInteractionFeedback(
    { interactionId },
    open && !!interactionId
  );

  useEffect(() => {
    if (isFeedbackError) {
      const errorMessage =
        feedbackError?.response?.data?.error || "Failed to load feedback.";
      showSnackbar("error", errorMessage);
      return;
    }

    if (!existingFeedback?.data || !isFeedbackSuccess) return;

    const feedback = existingFeedback?.data?.find(
      (f: any) =>
        f.key === feedbackKey &&
        f.feedbackReference === feedbackReference
    );
    if (feedback) {
      const option = feedback?.option || "";
      const subOption = feedback?.subOption || "";
      const commentValue = feedback?.additionalComments || "";

      setSelectedOption(option);
      setSelectedSubOption(subOption);
      setComment(commentValue);

      setInitialValues({
        option,
        subOption,
        comment: commentValue,
      });
    } else {
      setSelectedOption("");
      setSelectedSubOption("");
      setComment("");

      setInitialValues({
        option: "",
        subOption: "",
        comment: "",
      });
    }
  }, [
    existingFeedback,
    feedbackKey,
    feedbackReference,
    isFeedbackSuccess,
    isFeedbackError,
    feedbackError,
  ]);

  const { mutate: upsertFeedback } = InteractionFeedbackService.useUpsertInteractionFeedback();

  const formatLabel = (text: string) => {
    return text
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  useEffect(() => {
    if (isOptionsError) {
      const errorMessage = (optionsError as any)?.response?.data?.error || "Failed to load feedback options.";
      showSnackbar("error", errorMessage);
    }
  }, [isOptionsError, optionsError, showSnackbar]);

  const buildOptions = (data: any[], key: string) => {
    const filtered = data?.filter((item: any) => item.key === key) || [];
    const grouped: Record<string, any> = {};

    filtered.forEach((item) => {
      if (!grouped[item.option]) {
        grouped[item.option] = {
          label: formatLabel(item.option),
          value: item.option,
          subOptions: [],
        };
      }

      if (item.subOption) {
        grouped[item.option].subOptions.push({
          label: formatLabel(item.subOption),
          value: item.subOption,
        });
      }
    });

    return Object.values(grouped);
  };

  const optionsKey =
    feedbackKey.startsWith("abstraction_call_quality")
      ? "abstraction_call_quality"
      : feedbackKey === "client_action_item"
      ? "action_item"
      : feedbackKey;

  const options = isOptionSuccess
    ? buildOptions(feedbackOptionsData?.data || [], optionsKey)
    : [];

  useEffect(() => {
    if (open && !existingFeedback?.data) {
      setSelectedOption("");
      setSelectedSubOption("");
      setComment("");
      setInitialValues({
        option: "",
        subOption: "",
        comment: "",
      });
    }
  }, [open]);

  const isChanged =
    selectedOption !== initialValues?.option ||
    selectedSubOption !== initialValues?.subOption ||
    comment !== initialValues?.comment;

    useEffect(() => {
      if (open) {
        setShowOptionError(false);
        setShowCommentError(false);
      }
    }, [open]);

  const handleSubmit = () => {
    let hasError = false;

    const selectedOptionData = options?.find(
      (opt: any) => opt.value === selectedOption
    );

    if (!selectedOption) {
      setShowOptionError(true);
      hasError = true;
    }
    if (
      selectedOptionData?.subOptions?.length > 0 &&
      !selectedSubOption
    ) {
      setShowSubOptionError(true);
      hasError = true;
    }

    const commentRequired =
      !isActionItemFeedback ||
      (isActionItemFeedback && isOtherSelected);

    if (commentRequired && !comment.trim()) {
      setShowCommentError(true);
      hasError = true;
    }

    if (hasError) return;

    if (!isOptionSuccess || !feedbackOptionsData?.data) {
      showSnackbar("error", "Feedback options are not yet loaded. Please try again in a moment.");
      return;
    }

    const optionMatch = feedbackOptionsData.data.find(
      (item: any) =>
        item.key === optionsKey &&
        item.option === selectedOption &&
        (item.subOption || "") === (selectedSubOption || "")
    );

    if (!optionMatch) return;
    upsertFeedback(
      {
        interactionId,
        body: {
          feedbackReference: feedbackReference || "",
          key: feedbackKey,
          selectedFeedbackOptionId: optionMatch?.feedbackOptionId,
          additionalComments: comment || null,
        },
      },
      {
        onSuccess: () => refetchFeedback() // refresh feedback data
      }
    );
    onSubmit({
      option: selectedOption,
      subOption: selectedSubOption,
      comment,
    });

    onClose();
  };

  const isOtherSelected = selectedOption === "other";
  const disableSubmit =
    !selectedOption ||
    !isChanged ||
    (!isActionItemFeedback && !comment.trim()) ||
    (isActionItemFeedback && isOtherSelected && !comment.trim());

  const hasExistingFeedback =
    initialValues.option !== "" ||
    initialValues.subOption !== "" ||
    initialValues.comment !== "";
  
  const isLoading =
    isOptionsLoading ||
    isOptionsFetching ||
    isFeedbackLoading ||
    isFeedbackFetching;

  const popup = (
    <Box
      className={`feedback-popup ${
        anchorEl ? "feedback-popup--anchored" : ""
      }`}
    >
        {/* Header */}
        <Box className="feedback-popup__header">
        {feedbackKey === "topic" && (
          <Box className="feedback-popup__tag-wrapper">
            <Typography className="feedback-popup__title">
            Current Tag:
            </Typography>

            <Box className="topics__badge">
                {badgeData?.isPositive && (
                    <>
                    <img
                        src={PositiveIcon}
                        alt="Positive"
                        className="topics__positive-icon"
                        style={{
                            width: 16,
                            height: 16,
                            filter:
                              "invert(48%) sepia(84%) saturate(577%) hue-rotate(70deg) brightness(95%) contrast(88%)",
                        }}
                    />
                    <Typography sx={{ fontSize: "0.75rem" }}>
                        {badgeData?.topicSentiment ? formatLabel(badgeData.topicSentiment) : ""}
                    </Typography>
                    </>
                )}

                {(badgeData?.isNegative || badgeData?.isNeutral) && (
                    <>
                    <Box
                        component="img"
                        src={TopicSentimentIcon}
                        alt="classification"
                        className="topics__classification-icon"
                        sx={{
                            width: 16,
                            height: 16,
                            filter:
                            badgeData?.topicClassification?.replace(/_/g, " ") ===
                            "client controllable"
                            ? "invert(34%) sepia(99%) saturate(1914%) hue-rotate(199deg) brightness(95%) contrast(90%)"
                            : "invert(14%) sepia(91%) saturate(7461%) hue-rotate(293deg) brightness(97%) contrast(108%)",
                        }}
                        />
                    <Typography
                        sx={{
                        color: badgeData?.classificationColor,
                        fontSize: "0.75rem",
                        }}
                    >
                        {badgeData?.topicClassification ? formatLabel(badgeData.topicClassification) : ""}
                    </Typography>
                    </>
                )}
            </Box>
          </Box>
        )}
        {isActionItemFeedback && (
          <Box sx={{display: "flex", flexDirection: "column"}}>
          <Typography className="feedback-popup__title">
            Why did you discard this ?
          </Typography>
          <Typography>
            Help us improve AI suggestions
          </Typography>
          </Box>
        )}
        {feedbackKey === "abstraction_insights" && (
          <>
            <Typography className="feedback-popup__title">
              Current Classification:
              <span
              style={{
                marginLeft: "6px",
                fontWeight: 500,
                color:
                  score === "Positive"
                    ? "#32BD00"
                    : score === "Negative"
                    ? "#FF2E00"
                    : score === "Neutral"
                    ? "#FFC700"
                    : score === "Mixed"
                    ? "#127199"
                    : executiveAttentionNeeded?.toLowerCase() === "yes"
                    ? "#32BD00"
                    : executiveAttentionNeeded?.toLowerCase() === "no"
                    ? "#FF2E00"
                    : executiveAttentionNeeded?.toLowerCase() === "insufficient data"
                    ? "#FFC700"
                    : "#000",
              }}
            >
              {score || executiveAttentionNeeded}
            </span>
          </Typography>
          </>
        )}
        {feedbackKey.startsWith("abstraction_call_quality") && (
          <Typography className="feedback-popup__title">
            Current Classification:
            <span
              style={{
                marginLeft: 6,
                fontWeight: 600,
                color: answer?.toLowerCase() === "yes" ? "#2BB673" : "#FF2E00",
              }}
            >
              {answer}
            </span>
          </Typography>
        )}
        {feedbackKey === "prep_notes" && (
          <Typography className="feedback-popup__title">
            Provide feedback on : {feedbackReference}
          </Typography>
        )
        }
        {/* Close Button */}
        <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
        </IconButton>
        </Box>

        <Divider className="feedback-popup__divider"/>

        <Box className="feedback-popup__content">
          {isLoading ? (
          <Box
            sx={{
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={14} />
          </Box>
        ) : (
          <>
        {/* Main Options */}
        <FormControl error={showOptionError && !selectedOption}>
        <RadioGroup
          value={selectedOption}
          onChange={(e) => {
            setSelectedOption(e.target.value);
            setSelectedSubOption("");
            setShowOptionError(false);
          }}
        >
          {options?.map((opt: any) => (
            <Box key={opt.value}>
              <FormControlLabel
                value={opt.value}
                control={<Radio
                  className={`feedback-popup__radio ${
                    showOptionError && !selectedOption ? "radio-error" : ""
                  }`}
                  disabled={isImpersonating}
                />}
                label={opt.label}
              />

              {/* Sub options */}
              {selectedOption === opt.value && opt.subOptions?.length > 0 && (
                <RadioGroup
                  value={selectedSubOption}
                  onChange={(e) => {
                    setSelectedSubOption(e.target.value);
                    setShowSubOptionError(false);
                  }}
                  className="feedback-popup__sub-options"
                >
                  <Typography fontSize={13} color="gray">
                    Select correct tag:
                  </Typography>

                  {opt.subOptions.map((sub: any) => (
                    <FormControlLabel
                      key={sub.value}
                      value={sub.value}
                      control={
                        <Radio
                          className={`feedback-popup__radio feedback-popup__radio-sub ${
                            showSubOptionError && !selectedSubOption ? "radio-error" : ""
                          }`}
                          disabled={isImpersonating}
                        />
                      }
                      label={sub.label}
                    />
                  ))}
                </RadioGroup>
              )}
            </Box>
          ))}
        </RadioGroup>
        </FormControl>
        </>
        )}

        {/* Textarea */}
        <Typography className="feedback-popup__textarea-label">
          Please tell us why?{" "}
          <span style={{ color: "#6F6F7F" }}>
            {isActionItemFeedback && selectedOption !== "other"
              ? "(Optional)"
              : "(Required)"}
          </span>
          {(!isActionItemFeedback || selectedOption === "other") && (
            <span style={{ color: "#FF2E00", marginLeft: "2px",fontSize: "20px"}}>*</span>
          )}
        </Typography>

        <TextField
          multiline
          minRows={4}
          fullWidth
          value={comment}
          error={showCommentError}
          onChange={(e) => {
            setComment(e.target.value);
            if (e.target.value.trim()) {
              setShowCommentError(false);
            }
          }}
          disabled={isImpersonating}
          placeholder="Please share your thoughts..."
          sx={{
            mt: 1,
            "& .MuiInputBase-root": {
              height: "120px",
              alignItems: "flex-start",
            },
            "& textarea": {
              height: "100% !important",
              overflowY: "auto",
            },
          }}
        />

        {/* Buttons */}
        <Box className="feedback-popup__buttons">
            <button
              className={`btn btn-outlined-small ${!isChanged ? "disabled-look" : ""}`}
              disabled={!isChanged}
              onClick={() => {
                if (hasExistingFeedback) {
                  setSelectedOption(initialValues.option);
                  setSelectedSubOption(initialValues.subOption);
                  setComment(initialValues.comment);
                } else {
                  onClose();
                }
              }}
            >
              Cancel
            </button>

            <button
              className={`btn btn-purple-compact ${
                disableSubmit ? "disabled-look" : ""
              }`}
              onClick={handleSubmit}
            >
              Submit
            </button>
          </Box>
        </Box>
    </Box>
  );

  if (anchorEl) {
    return (
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="left-start"
        modifiers={[
          {
            name: "flip",
            enabled: true,
            options: {
              fallbackPlacements: [
                "left-end",
              ],
            },
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              padding: 8,
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 100],
            },
          },
        ]}
        style={{ zIndex: 1500 }}
      >
        <Paper
          sx={{
            overflow: "visible",
            backgroundColor: "transparent",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          {popup}
        </Paper>
      </Popper>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      {popup}
    </Modal>
  );
};

export default FeedbackPopup;
